import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Search } from 'lucide-react'
import { ActionButton, Badge, Button, Card } from '@/components/ui'
import { useAuthStore } from '@/store'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import {
  assignPersonalManager,
  listPersonalManagerPartnerCoverage,
  listPersonalManagers,
  transferPersonalManager,
  unassignPersonalManager,
  updatePersonalManagerAssignmentStart,
} from '@/services/personalManagerService'
import type { PersonalManagerListItem, PersonalManagerPartnerCoverageRow } from '@/types'
import { formatPartnerDisplay } from '@/lib/utils'
import { assignmentCloseActionLabel, describeAssignmentClose } from '@/lib/assignmentBoundary'

type CoverageFilter = 'all' | 'assigned' | 'unassigned' | 'attention'

type CoverageGroup = {
  key: string
  managerName: string
  rows: PersonalManagerPartnerCoverageRow[]
}

function formatLocalDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  const date = new Date(`${value}T00:00:00`)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('ru-RU')
}

function getCoverageFilter(searchParams: URLSearchParams): CoverageFilter {
  if (searchParams.get('attention') === '1') return 'attention'
  const mode = searchParams.get('mode')
  if (mode === 'assigned' || mode === 'unassigned') return mode
  return 'all'
}

function getManagerLabel(row: PersonalManagerPartnerCoverageRow): string {
  if (!row.manager_user_id) return 'Без ЛМ'
  return row.manager_name || `#${row.manager_user_id.slice(0, 8)}`
}

function attentionLabel(reason: PersonalManagerPartnerCoverageRow['attention_reason']): string | null {
  if (reason === 'no_active_studios') return 'Нет активных студий'
  if (reason === 'manager_inactive') return 'ЛМ деактивирован'
  if (reason === 'manager_role_mismatch') return 'Роль ЛМ снята'
  return null
}

function canBulkAssign(row: PersonalManagerPartnerCoverageRow): boolean {
  return row.attention_reason !== 'no_active_studios'
}

function sortCoverageRows(a: PersonalManagerPartnerCoverageRow, b: PersonalManagerPartnerCoverageRow): number {
  if (a.assignment_needs_attention !== b.assignment_needs_attention) {
    return a.assignment_needs_attention ? -1 : 1
  }
  return getManagerLabel(a).localeCompare(getManagerLabel(b), 'ru')
    || a.partner_number - b.partner_number
}

function groupCoverageRows(rows: PersonalManagerPartnerCoverageRow[]): CoverageGroup[] {
  const groups = new Map<string, CoverageGroup>()
  for (const row of rows) {
    const key = row.manager_user_id || 'unassigned'
    const existing = groups.get(key)
    if (existing) existing.rows.push(row)
    else groups.set(key, { key, managerName: getManagerLabel(row), rows: [row] })
  }
  return Array.from(groups.values()).sort((a, b) => {
    if (a.key === 'unassigned') return -1
    if (b.key === 'unassigned') return 1
    const aAttention = a.rows.some(row => row.assignment_needs_attention)
    const bAttention = b.rows.some(row => row.assignment_needs_attention)
    if (aAttention !== bAttention) return aAttention ? -1 : 1
    return a.managerName.localeCompare(b.managerName, 'ru')
  })
}

export function PersonalManagersCoveragePage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuthStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)

  const [rows, setRows] = useState<PersonalManagerPartnerCoverageRow[]>([])
  const [managers, setManagers] = useState<PersonalManagerListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [focusManager, setFocusManager] = useState('all')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [chosenManager, setChosenManager] = useState('')
  const [assignedFrom, setAssignedFrom] = useState(() => formatLocalDate(new Date()))
  const [busy, setBusy] = useState(false)
  const [isAdvanced, setIsAdvanced] = useState<boolean | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmStep, setConfirmStep] = useState<'review' | 'final'>('review')
  const [closeAssignmentRow, setCloseAssignmentRow] = useState<PersonalManagerPartnerCoverageRow | null>(null)
  const [closeAssignmentDate, setCloseAssignmentDate] = useState(() => formatLocalDate(new Date()))
  const [closeAssignmentBusy, setCloseAssignmentBusy] = useState(false)
  const coverageFilter = getCoverageFilter(searchParams)

  const reload = () => {
    setLoading(true)
    Promise.all([listPersonalManagerPartnerCoverage(), listPersonalManagers()])
      .then(([coverageRows, managerRows]) => {
        setRows(coverageRows)
        setManagers(managerRows)
        setError(null)
      })
      .catch(err => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false))
  }

  useEffect(() => { reload() }, [])

  useEffect(() => {
    let cancelled = false
    if (!user?.id) {
      setIsAdvanced(false)
      return
    }
    getAccessLevel().then(level => {
      if (!cancelled) setIsAdvanced(level === 'advanced')
    })
    return () => { cancelled = true }
  }, [user?.id, getAccessLevel])

  const focusManagerOptions = useMemo(() => {
    const options = new Map<string, { id: string; name: string; inactive: boolean }>()
    for (const manager of managers) {
      options.set(manager.manager_user_id, {
        id: manager.manager_user_id,
        name: manager.manager_name || `#${manager.manager_user_id.slice(0, 8)}`,
        inactive: false,
      })
    }
    for (const row of rows) {
      if (!row.manager_user_id) continue
      const existing = options.get(row.manager_user_id)
      options.set(row.manager_user_id, {
        id: row.manager_user_id,
        name: row.manager_name || existing?.name || `#${row.manager_user_id.slice(0, 8)}`,
        inactive: !row.manager_role_valid,
      })
    }
    return Array.from(options.values()).sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  }, [managers, rows])

  const searchFilteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const num = q ? Number(q) : null
    return rows.filter(row => {
      if (!q) return true
      if (num !== null && !Number.isNaN(num) && row.partner_number === num) return true
      if (row.manager_name?.toLowerCase().includes(q)) return true
      if (row.persons_fio?.toLowerCase().includes(q)) return true
      return Boolean(row.cities_text?.toLowerCase().includes(q))
    })
  }, [rows, search])

  const stats = useMemo(() => {
    const activeRows = rows.filter(row => row.has_active_studios)
    const without = activeRows.filter(row => !row.manager_user_id).length
    const attention = rows.filter(row => row.assignment_needs_attention).length
    const all = rows.filter(row => row.has_active_studios || row.assignment_needs_attention).length
    const assigned = activeRows.filter(row => row.manager_user_id && !row.assignment_needs_attention).length
    return { all, with: assigned, without, attention }
  }, [rows])

  const visibleRows = useMemo(() => searchFilteredRows
    .filter(row => {
      if (focusManager !== 'all' && row.manager_user_id !== focusManager) return false
      if (coverageFilter === 'attention') return row.assignment_needs_attention
      if (coverageFilter === 'assigned') return Boolean(row.manager_user_id) && !row.assignment_needs_attention
      if (coverageFilter === 'unassigned') return row.has_active_studios && !row.manager_user_id
      return row.has_active_studios || row.assignment_needs_attention
    })
    .sort(sortCoverageRows), [coverageFilter, focusManager, searchFilteredRows])

  const coverageGroups = useMemo(() => groupCoverageRows(visibleRows), [visibleRows])
  const selectedVisibleCount = useMemo(
    () => visibleRows.filter(row => selected.has(row.partner_number)).length,
    [selected, visibleRows]
  )

  const toggleSelect = (row: PersonalManagerPartnerCoverageRow) => {
    if (!canBulkAssign(row)) return
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(row.partner_number)) next.delete(row.partner_number)
      else next.add(row.partner_number)
      return next
    })
  }

  const selectedRows = useMemo(
    () => rows.filter(row => selected.has(row.partner_number) && canBulkAssign(row)),
    [rows, selected]
  )
  const transferCount = useMemo(
    () => selectedRows.filter(row => row.manager_user_id && row.manager_user_id !== chosenManager).length,
    [selectedRows, chosenManager]
  )
  const chosenManagerName = useMemo(
    () => managers.find(manager => manager.manager_user_id === chosenManager)?.manager_name
      || (chosenManager ? `#${chosenManager.slice(0, 8)}` : ''),
    [managers, chosenManager]
  )

  const diff = useMemo(() => {
    const newAssign: PersonalManagerPartnerCoverageRow[] = []
    const transfer: PersonalManagerPartnerCoverageRow[] = []
    const dateChange: PersonalManagerPartnerCoverageRow[] = []
    const noChange: PersonalManagerPartnerCoverageRow[] = []
    for (const row of selectedRows) {
      if (!row.manager_user_id) newAssign.push(row)
      else if (row.manager_user_id !== chosenManager) transfer.push(row)
      else if (row.assigned_from !== assignedFrom) dateChange.push(row)
      else noChange.push(row)
    }
    return { newAssign, transfer, dateChange, noChange }
  }, [selectedRows, chosenManager, assignedFrom])

  const effectiveCount = diff.newAssign.length + diff.transfer.length + diff.dateChange.length

  const setCoverageFilter = (value: CoverageFilter) => {
    const next = new URLSearchParams(searchParams)
    if (value === 'attention') next.set('attention', '1')
    else next.delete('attention')
    if (value === 'assigned' || value === 'unassigned') next.set('mode', value)
    else next.delete('mode')
    setSearchParams(next, { replace: true })
    setSelected(new Set())
  }

  const openCloseAssignment = (row: PersonalManagerPartnerCoverageRow) => {
    if (!row.assignment_id) return
    setActionError(null)
    setCloseAssignmentRow(row)
    setCloseAssignmentDate(row.suggested_unassigned_at || formatLocalDate(new Date()))
  }

  const closeAssignmentOutcome = describeAssignmentClose(closeAssignmentDate, closeAssignmentRow?.assigned_from)

  const handleCloseAssignment = async () => {
    if (!closeAssignmentRow?.assignment_id || !closeAssignmentDate) return
    const outcome = describeAssignmentClose(closeAssignmentDate, closeAssignmentRow.assigned_from)
    if (outcome.removed && !window.confirm(
      'Дата не позже начала назначения, поэтому связь будет УДАЛЕНА целиком, а не закрыта. '
      + 'Восстановить её из этого экрана будет нельзя. Продолжить?'
    )) return

    setCloseAssignmentBusy(true)
    setActionError(null)
    try {
      await unassignPersonalManager({
        assignmentId: closeAssignmentRow.assignment_id,
        unassignedAt: closeAssignmentDate,
        note: closeAssignmentRow.attention_reason === 'no_active_studios'
          ? 'Связь завершена из контроля назначений без активных студий'
          : 'Назначение снято из контроля назначений ЛМ',
      })
      setCloseAssignmentRow(null)
      reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setCloseAssignmentBusy(false)
    }
  }

  const handleApply = async () => {
    if (!chosenManager || selectedRows.length === 0) return
    setBusy(true)
    setActionError(null)
    try {
      for (const row of selectedRows) {
        if (row.manager_user_id === chosenManager) {
          if (row.assignment_id && row.assigned_from !== assignedFrom) {
            await updatePersonalManagerAssignmentStart({ assignmentId: row.assignment_id, assignedFrom })
          }
          continue
        }
        if (row.manager_user_id) {
          await transferPersonalManager({
            partnerNumber: row.partner_number,
            newManagerUserId: chosenManager,
            effectiveFrom: assignedFrom,
          })
        } else {
          await assignPersonalManager({
            managerUserId: chosenManager,
            partnerNumber: row.partner_number,
            assignedFrom,
          })
        }
      }
      setSelected(new Set())
      setConfirmOpen(false)
      reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (isAdvanced === false) {
    return <Card><div className="text-sm text-muted-foreground">Эта страница доступна только пользователям УК с уровнем доступа «advanced».</div></Card>
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <button type="button" onClick={() => navigate('/management/personal-managers')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={14} /> К списку ЛМ
        </button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Назначения франчайзи</h1>
          <p className="mt-1 text-sm text-muted-foreground">Распределение франчайзи между личными менеджерами.</p>
        </div>
      </div>

      <Card className="border border-border shadow-sm">
        <div className="grid gap-3 lg:grid-cols-[minmax(260px,1fr)_240px]">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input type="text" value={search} onChange={event => setSearch(event.target.value)} placeholder="Поиск: № франчайзи, ФИО, город или ЛМ" className="w-full pl-9 pr-3 py-2 border border-border rounded-md bg-background text-sm" />
          </div>
          <select value={focusManager} onChange={event => setFocusManager(event.target.value)} className="input h-10 w-full px-3 py-1 text-sm">
            <option value="all">Все личные менеджеры</option>
            {focusManagerOptions.map(manager => (
              <option key={manager.id} value={manager.id}>{manager.name}{manager.inactive ? ' · требует проверки' : ''}</option>
            ))}
          </select>
        </div>
        <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1">
          <FilterButton active={coverageFilter === 'all'} onClick={() => setCoverageFilter('all')}>Все · {stats.all}</FilterButton>
          <FilterButton active={coverageFilter === 'assigned'} onClick={() => setCoverageFilter('assigned')}>Назначенные · {stats.with}</FilterButton>
          <FilterButton active={coverageFilter === 'unassigned'} onClick={() => setCoverageFilter('unassigned')}>Без ЛМ · {stats.without}</FilterButton>
          <FilterButton active={coverageFilter === 'attention'} onClick={() => setCoverageFilter('attention')}>Требуют проверки · {stats.attention}</FilterButton>
        </div>
      </Card>

      {(selectedVisibleCount > 0 || focusManager !== 'all') && (
        <div className="flex flex-wrap items-center gap-2">
          {selectedVisibleCount > 0 && <Badge text={`Выбрано на экране: ${selectedVisibleCount}`} variant="success" className="text-xs" />}
          {focusManager !== 'all' && <Badge text={`ЛМ: ${focusManagerOptions.find(manager => manager.id === focusManager)?.name || 'личный менеджер'}`} variant="outline" className="text-xs" />}
        </div>
      )}

      {selected.size > 0 && (
        <Card className="border border-primary/30 bg-primary/5">
          <div className="space-y-3">
            <div className="text-sm font-semibold">Назначить {selectedRows.length} франчайзи</div>
            <div className="flex flex-wrap gap-2">
              <select value={chosenManager} onChange={event => setChosenManager(event.target.value)} className="flex-1 min-w-[200px] px-3 py-2 border border-border rounded-md bg-background text-sm">
                <option value="">Выберите ЛМ...</option>
                {managers.map(manager => (
                  <option key={manager.manager_user_id} value={manager.manager_user_id}>{manager.manager_name || `#${manager.manager_user_id.slice(0, 8)}`} · {manager.partners_count} франч.</option>
                ))}
              </select>
              <input type="date" value={assignedFrom} onChange={event => setAssignedFrom(event.target.value)} className="px-3 py-2 border border-border rounded-md bg-background text-sm" />
              <Button size="sm" disabled={!chosenManager || busy || selectedRows.length === 0} onClick={() => { setActionError(null); setConfirmStep('review'); setConfirmOpen(true) }}>Применить</Button>
              <Button size="sm" variant="outline" onClick={() => setSelected(new Set())}>Очистить выбор</Button>
            </div>
            {transferCount > 0 && <div className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle size={12} />{transferCount} франчайзи будут переданы от текущего ЛМ; предыдущий период закроется {assignedFrom} − 1 день.</div>}
            {actionError && <div className="text-xs text-destructive">Ошибка: {actionError}</div>}
          </div>
        </Card>
      )}

      {loading && <Card><div className="text-sm text-muted-foreground">Загрузка...</div></Card>}
      {error && <Card><div className="text-sm text-destructive">Ошибка: {error}</div></Card>}
      {!loading && !error && visibleRows.length > 0 && <CoverageTable groups={coverageGroups} selected={selected} onToggleSelect={toggleSelect} onCloseAssignment={openCloseAssignment} />}
      {!loading && !error && visibleRows.length === 0 && <Card><div className="text-sm text-muted-foreground">Ничего не найдено.</div></Card>}

      {closeAssignmentRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => { if (!closeAssignmentBusy) setCloseAssignmentRow(null) }}>
          <div className="w-full max-w-lg rounded-card bg-card shadow-lg" onClick={event => event.stopPropagation()}>
            <div className="border-b border-border px-4 py-3">
              <div className="text-sm font-semibold">Снять назначение личного менеджера</div>
              <div className="mt-1 text-xs text-muted-foreground">{formatPartnerDisplay(closeAssignmentRow.partner_number, closeAssignmentRow.persons_fio, closeAssignmentRow.cities_text)}</div>
            </div>
            <div className="space-y-4 px-4 py-4">
              {closeAssignmentRow.attention_reason === 'no_active_studios' ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                  <div className="font-medium text-amber-800">Нет активных студий</div>
                  <div className="mt-1 text-xs text-muted-foreground">Связь с ЛМ осталась и продолжает считаться действующей.</div>
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                  <div className="font-medium text-amber-800">Франчайзи останется без ЛМ</div>
                  <div className="mt-1 text-xs text-muted-foreground">Новый ЛМ автоматически назначен не будет. При необходимости назначьте его отдельным действием.</div>
                </div>
              )}
              <label className="block">
                <span className="text-xs font-medium text-muted-foreground">Первый день без назначения</span>
                <input type="date" value={closeAssignmentDate} onChange={event => setCloseAssignmentDate(event.target.value)} className="input mt-1 h-9 w-full px-3 py-1 text-sm" />
                <span className="mt-1 block text-xs font-medium text-foreground">
                  {closeAssignmentOutcome.removed ? 'назначение будет удалено полностью' : closeAssignmentOutcome.lastActiveDay ? `числится по ${formatDate(closeAssignmentOutcome.lastActiveDay)} включительно` : 'укажите дату'}
                </span>
              </label>
              {closeAssignmentRow.has_active_studios ? (
                <div className="text-xs text-muted-foreground">Выбранная дата — первый день, когда личный менеджер уже не отвечает за франчайзи.</div>
              ) : closeAssignmentRow.suggested_unassigned_at ? (
                <div className="text-xs text-muted-foreground">Предлагаемая дата рассчитана по последнему дню активной студии{closeAssignmentRow.last_active_studio_on ? ` (${formatDate(closeAssignmentRow.last_active_studio_on)})` : ''}.</div>
              ) : closeAssignmentRow.has_future_studios ? (
                <div className="text-xs text-amber-800">У франчайзи есть студия с будущей датой начала. Проверьте, должна ли связь сохраняться.</div>
              ) : <div className="text-xs text-muted-foreground">Проверьте дату завершения вручную.</div>}
              {closeAssignmentOutcome.removed && closeAssignmentRow.assigned_from && <div className="text-xs text-amber-800">Дата не позже начала назначения ({formatDate(closeAssignmentRow.assigned_from)}), поэтому связь будет удалена целиком.</div>}
              {actionError && <div className="text-xs text-error">{actionError}</div>}
            </div>
            <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
              <Button size="sm" variant="outline" disabled={closeAssignmentBusy} onClick={() => setCloseAssignmentRow(null)}>Отмена</Button>
              <Button size="sm" disabled={closeAssignmentBusy || !closeAssignmentDate} onClick={() => void handleCloseAssignment()}>{closeAssignmentBusy ? 'Завершаем…' : assignmentCloseActionLabel(closeAssignmentDate, formatLocalDate(new Date()))}</Button>
            </div>
          </div>
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => { if (!busy) setConfirmOpen(false) }}>
          <div className="bg-card rounded-card shadow-lg w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={event => event.stopPropagation()}>
            <div className="px-4 py-3 border-b border-border">
              <div className="text-sm font-semibold">Подтверждение назначения</div>
              <div className="text-xs text-muted-foreground mt-0.5">Назначить ЛМ <span className="text-foreground font-medium">{chosenManagerName}</span> с <span className="text-foreground font-medium">{assignedFrom}</span>. Выбрано {selectedRows.length}, изменений: {effectiveCount}.</div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 text-sm">
              {effectiveCount === 0 && <div className="text-muted-foreground">Все выбранные франчайзи уже назначены этому ЛМ с указанной даты.</div>}
              {diff.newAssign.length > 0 && <DiffSection title={`Новые назначения · ${diff.newAssign.length}`} accent="success">{diff.newAssign.map(row => <DiffRow key={row.partner_number}><PartnerLabel row={row} /><span className="text-xs text-muted-foreground">ранее без ЛМ → <span className="text-foreground">{chosenManagerName}</span> с {assignedFrom}</span></DiffRow>)}</DiffSection>}
              {diff.transfer.length > 0 && <DiffSection title={`Передача от другого ЛМ · ${diff.transfer.length}`} accent="warning"><div className="text-[11px] text-muted-foreground mb-1.5 flex items-center gap-1"><AlertTriangle size={11} />Предыдущее назначение закроется датой {assignedFrom} − 1 день</div>{diff.transfer.map(row => <DiffRow key={row.partner_number}><PartnerLabel row={row} /><span className="text-xs text-muted-foreground">от <span className="text-foreground">{row.manager_name}</span>{row.assigned_from && <> (с {row.assigned_from})</>} → <span className="text-foreground">{chosenManagerName}</span> с {assignedFrom}</span></DiffRow>)}</DiffSection>}
              {diff.dateChange.length > 0 && <DiffSection title={`Изменение даты начала · ${diff.dateChange.length}`} accent="neutral">{diff.dateChange.map(row => <DiffRow key={row.partner_number}><PartnerLabel row={row} /><span className="text-xs text-muted-foreground">тот же ЛМ: дата {row.assigned_from ?? '—'} → <span className="text-foreground">{assignedFrom}</span></span></DiffRow>)}</DiffSection>}
              {diff.noChange.length > 0 && <DiffSection title={`Без изменений · ${diff.noChange.length}`} accent="muted">{diff.noChange.map(row => <DiffRow key={row.partner_number}><PartnerLabel row={row} /><span className="text-xs text-muted-foreground">уже у {chosenManagerName} с {row.assigned_from ?? '—'} — будет пропущен</span></DiffRow>)}</DiffSection>}
              {actionError && <div className="text-xs text-destructive flex items-center gap-1"><AlertTriangle size={12} /> {actionError}</div>}
            </div>
            {confirmStep === 'final' && <div className="px-4 py-3 border-t border-border bg-error/5"><div className="text-sm font-semibold text-error flex items-center gap-2"><AlertTriangle size={14} />Окончательное подтверждение</div><div className="text-xs text-muted-foreground mt-1">Будет применено {effectiveCount} изменени{effectiveCount === 1 ? 'е' : 'й'}{diff.transfer.length > 0 && <>, включая передачу {diff.transfer.length} франчайзи от другого ЛМ</>}.</div></div>}
            <div className="px-4 py-3 border-t border-border flex flex-wrap items-center justify-end gap-2">
              {confirmStep === 'review' ? <><Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirmOpen(false)}>Отмена</Button><Button size="sm" disabled={busy || effectiveCount === 0} onClick={() => setConfirmStep('final')}>Подтвердить ({effectiveCount})</Button></> : <><Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirmStep('review')}>Назад</Button><Button size="sm" variant="secondary" disabled={busy || effectiveCount === 0} onClick={handleApply}>{busy ? 'Применяем…' : 'Да, применить'}</Button></>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function FilterButton({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  const activeClass = 'border-primary bg-primary text-primary-foreground shadow-sm'
  const idleClass = 'border-border bg-card text-foreground hover:bg-accent'
  return <button type="button" onClick={onClick} className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold leading-none transition ${active ? activeClass : idleClass}`}>{children}</button>
}

function CoverageTable({ groups, selected, onToggleSelect, onCloseAssignment }: { groups: CoverageGroup[]; selected: Set<number>; onToggleSelect: (row: PersonalManagerPartnerCoverageRow) => void; onCloseAssignment: (row: PersonalManagerPartnerCoverageRow) => void }) {
  return (
    <Card className="overflow-hidden border border-border shadow-sm">
      <div className="border-b border-border px-4 py-2"><div className="text-sm font-semibold leading-tight">Реестр назначений</div><div className="mt-0.5 text-xs leading-snug text-muted-foreground">Одна строка — один франчайзи. ЛМ сгруппирован слева, проблемы и действия видны отдельно.</div></div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1000px] border-separate border-spacing-0 text-[13px] leading-snug">
          <thead className="bg-muted/50 text-xs text-muted-foreground"><tr><th className="w-[210px] border-b border-border px-3 py-2 text-left font-semibold">Личный менеджер</th><th className="w-10 border-b border-border px-2 py-2"></th><th className="border-b border-border px-3 py-2 text-left font-semibold">Франчайзи</th><th className="w-[150px] border-b border-border px-3 py-2 text-left font-semibold">Контур</th><th className="w-[170px] border-b border-border px-3 py-2 text-left font-semibold">Период</th><th className="w-[190px] border-b border-border px-3 py-2 text-left font-semibold">Статус</th><th className="w-[140px] border-b border-border px-3 py-2 text-right font-semibold">Действие</th></tr></thead>
          <tbody>{groups.map(group => {
            const groupStudios = group.rows.reduce((sum, row) => sum + row.studio_count, 0)
            const groupAttention = group.rows.filter(row => row.assignment_needs_attention).length
            return group.rows.map((row, index) => {
              const isSelected = selected.has(row.partner_number)
              const selectable = canBulkAssign(row)
              const status = attentionLabel(row.attention_reason)
              return (
                <tr key={row.partner_number} className={isSelected ? 'bg-primary/5' : 'bg-card'}>
                  {index === 0 && <td rowSpan={group.rows.length} className="border-b border-border/70 px-3 py-2 align-top"><div className="font-medium leading-snug">{group.managerName}</div><div className="mt-1 text-xs text-muted-foreground">{group.rows.length} франч. · {groupStudios} студ.</div>{groupAttention > 0 && <div className="mt-1.5"><Badge text={`Проверить: ${groupAttention}`} variant="warning" className="text-[11px]" /></div>}</td>}
                  <td className="border-b border-border/70 px-2 py-2 align-top"><input type="checkbox" checked={isSelected} onChange={() => onToggleSelect(row)} className="mt-0.5 h-4 w-4" disabled={!selectable} title={selectable ? 'Выбрать для назначения или передачи' : 'Сначала завершите связь без активных студий'} /></td>
                  <td className="border-b border-border/70 px-3 py-2 align-top"><div className="font-medium leading-snug">{formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text)}</div>{row.cities_text && <div className="mt-0.5 text-xs text-muted-foreground">{row.cities_text}</div>}</td>
                  <td className="border-b border-border/70 px-3 py-2 align-top text-xs"><div className="font-medium text-foreground">{row.studio_count} студий</div>{row.has_future_studios && <div className="mt-0.5 text-amber-800">есть будущие</div>}</td>
                  <td className="border-b border-border/70 px-3 py-2 align-top text-xs text-muted-foreground">{row.assigned_from ? <div>с {formatDate(row.assigned_from)}</div> : <div>не назначен</div>}{row.assigned_to && <div>по {formatDate(row.assigned_to)}</div>}{row.last_active_studio_on && <div className="mt-0.5">студии до {formatDate(row.last_active_studio_on)}</div>}</td>
                  <td className="border-b border-border/70 px-3 py-2 align-top">{status ? <Badge text={status} variant="warning" className="whitespace-nowrap text-[11px]" /> : row.assignment_is_future ? <Badge text="запланирован" variant="outline" className="whitespace-nowrap text-[11px]" /> : row.manager_user_id ? <Badge text="назначен" variant="success" className="whitespace-nowrap text-[11px]" /> : <Badge text="без ЛМ" variant="warning" className="whitespace-nowrap text-[11px]" />}{(row.attention_reason === 'manager_inactive' || row.attention_reason === 'manager_role_mismatch') && <div className="mt-1 text-[11px] text-muted-foreground">Выберите строку и передайте новому ЛМ</div>}</td>
                  <td className="border-b border-border/70 px-3 py-2 text-right align-top">{row.assignment_id ? <ActionButton onClick={() => onCloseAssignment(row)}>{row.attention_reason === 'no_active_studios' ? 'Завершить' : 'Снять'}</ActionButton> : <span className="text-xs text-muted-foreground">—</span>}</td>
                </tr>
              )
            })
          })}</tbody>
        </table>
      </div>
    </Card>
  )
}

function DiffSection({ title, accent, children }: { title: string; accent: 'success' | 'warning' | 'neutral' | 'muted'; children: React.ReactNode }) {
  const accentClasses = { success: 'border-success/40 bg-success/5', warning: 'border-primary/40 bg-primary/5', neutral: 'border-border bg-muted/30', muted: 'border-border/60 bg-muted/10' }[accent]
  const titleClasses = { success: 'text-success', warning: 'text-primary', neutral: 'text-foreground', muted: 'text-muted-foreground' }[accent]
  return <div className={`rounded-md border ${accentClasses} p-2.5`}><div className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${titleClasses}`}>{title}</div><div className="space-y-1">{children}</div></div>
}

function DiffRow({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">{children}</div>
}

function PartnerLabel({ row }: { row: PersonalManagerPartnerCoverageRow }) {
  return <span className="text-sm font-medium">{formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text)}</span>
}
